import { Check, X } from 'lucide-react';
import { validatePassword } from '@/utils/passwordSecurity';

interface PasswordStrengthIndicatorProps {
  password: string;
  showRequirements?: boolean;
}

export function PasswordStrengthIndicator({
  password,
  showRequirements = true,
}: PasswordStrengthIndicatorProps) {
  if (!password) return null;

  const { score, strengthLabel, requirements } = validatePassword(password);

  const getBarColor = () => {
    switch (score) {
      case 1:
        return 'bg-rose-500';
      case 2:
        return 'bg-orange-500';
      case 3:
        return 'bg-amber-500';
      case 4:
        return 'bg-blue-500';
      case 5:
        return 'bg-emerald-500';
      default:
        return 'bg-white/10';
    }
  };

  const getTextColor = () => {
    switch (score) {
      case 1:
        return 'text-rose-400';
      case 2:
        return 'text-orange-400';
      case 3:
        return 'text-amber-400';
      case 4:
        return 'text-blue-400';
      case 5:
        return 'text-emerald-400';
      default:
        return 'text-khiimori-text-muted';
    }
  };

  return (
    <div className="space-y-2 mt-2 pt-1 text-xs">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-khiimori-text-secondary">Password strength:</span>
        <span className={`font-semibold ${getTextColor()}`}>{strengthLabel}</span>
      </div>

      <div className="grid grid-cols-5 gap-1.5 h-1.5 w-full">
        {[1, 2, 3, 4, 5].map((step) => (
          <div
            key={step}
            className={`h-full rounded-full transition-all duration-300 ${
              step <= score ? getBarColor() : 'bg-khiimori-bg-tertiary'
            }`}
          />
        ))}
      </div>

      {showRequirements && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1.5">
          {requirements.map((req) => (
            <div
              key={req.id}
              className={`flex items-center gap-1.5 text-[11px] transition-colors ${
                req.valid
                  ? 'text-emerald-400'
                  : 'text-khiimori-text-secondary/60'
              }`}
            >
              {req.valid ? (
                <Check className="w-3.5 h-3.5 shrink-0 text-emerald-400" />
              ) : (
                <X className="w-3.5 h-3.5 shrink-0 text-khiimori-text-secondary/40" />
              )}
              <span>{req.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
